import dynamic from 'next/dynamic';
import React, { type FC, memo, useState, type JSX } from 'react';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import type { CtaCallback } from '@/app/components/search/components/CityCardsGrid';
import { createIndexUrl, createQueryString } from '@/utils/queryUtils.ts';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import {
  MapCard,
  MapImage,
  OpenxUiCard,
  OpenxUiCardBackground,
  OpenxUiCardContent,
  OpenxUiCardDetailsBottomMobile,
  OpenxUiCardDetailsLeft,
  OpenxUiCardDetailsLeftFlight,
  OpenxUiCardGradient,
} from './styled';
import type { CityCard } from '../../../utils/buildCityCards/cityCardTypes';
import { ClickEventNameEnum } from '../../../Curiosity/Analytics/utils/ClickEventNameEnum';
import { sumClasses } from '../../../utils/misc';

const Price = dynamic(() => import('./Price'), { ssr: false });
const Rating = dynamic(() => import('./Rating'), { ssr: false });
const Details = dynamic(() => import('./Details'), { ssr: false });
const Label = dynamic(() => import('./Label'), { ssr: false });

enum CARD_LAYOUTS {
  CITY_MAP_CARD = 'CITY_MAP_CARD',
  CITY_CARD = 'CITY_CARD',
}

type OpenXCityCardProps = {
  cityCard: CityCard;
  isLoading: boolean;
  ctaCallBack: CtaCallback;
  cardPosition: number;
};

const OpenXCityCard: FC<OpenXCityCardProps> = memo(
  ({ cityCard, isLoading, ctaCallBack, cardPosition }: OpenXCityCardProps): JSX.Element | null => {
    const isClient = useIsClient();
    const [expandDetails, setExpandDetails] = useState(false);
    const {
      cardDictionary: { product, directFlight, accomodationType, mapCardCta, mapCardText },
      dealDetails: { imageUrl, price, cityName, countryName, description, stars, nightsText },
      className,
      appending,
      newSearchParams,
      isMapCard,
    } = cityCard;
    const { commonData } = usePageWrapperData();
    const { isDesktop } = useCommonClient();
    const { isMobileDevice, isTabletDevice, isDesktopDevice } = commonData;
    const deepLink = createIndexUrl(createQueryString(newSearchParams));

    const addWidthToImageUrl = () => {
      // Deliberately host-agnostic: a host-specific pattern would silently stop matching and drop
      // the per-position width. h_298 must stay in step with CDN_CLOUD_PATH in buildCityCards.
      const regex = /(h_298,)/;
      if (isMobileDevice) return imageUrl.replace(regex, '$1w_767,');

      if (isDesktopDevice) {
        switch (cardPosition % 16) {
          case 0:
          case 1:
          case 2:
          case 3:
          case 5:
          case 7:
          case 9:
          case 11:
          case 12:
          case 13:
          case 14:
            return imageUrl.replace(regex, '$1w_298,');
          case 4:
          case 6:
            return imageUrl.replace(regex, '$1w_455,');
          case 8:
          case 10:
          case 15:
            return imageUrl.replace(regex, '$1w_612,');
          default:
            return imageUrl;
        }
      }

      if (isTabletDevice) {
        switch (cardPosition % 8) {
          case 0:
          case 1:
          case 2:
          case 5:
          case 8:
            return imageUrl.replace(regex, '$1w_382,');
          case 3:
          case 4:
            return imageUrl.replace(regex, '$1w_580,');
          case 6:
          case 7:
            return imageUrl.replace(regex, '$1w_779,');
          default:
            return imageUrl;
        }
      }

      return imageUrl;
    };

    if (isLoading && !isMapCard) {
      return (
        <OpenxUiCard className={sumClasses(['openx-ui-card openx-ui-card-loading', className || ''])}>
          <div className="openx-ui-card-loading-title" />
          <div className="openx-ui-card-loading-left-big" />
          <div className="openx-ui-card-loading-left-small" />
          <div className="openx-ui-card-loading-right-big" />
          <div className="openx-ui-card-loading-right-small" />
        </OpenxUiCard>
      );
    }

    const handleCtaCallBack = (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLDivElement, MouseEvent>
    ): boolean => {
      e?.preventDefault();
      ctaCallBack({
        payload: {
          event_name: ClickEventNameEnum.DestinationCard,
          event_subcontext: 'card',
          event_value: deepLink,
          event_value_additional: isMapCard ? CARD_LAYOUTS.CITY_MAP_CARD : CARD_LAYOUTS.CITY_CARD,
        },
        newSearchParams,
      });
      return false;
    };

    if (isMapCard) {
      return (
        <OpenxUiCard
          href={deepLink}
          className={sumClasses(['openx-ui-card', className || ''])}
          onClick={(e) => handleCtaCallBack(e)}
          isMapCard={isMapCard}
        >
          <MapImage src={imageUrl} alt="" loading={isDesktop ? 'eager' : 'lazy'} />
          <MapCard>
            <div>{mapCardText}</div>
            <button>{mapCardCta}</button>
          </MapCard>
        </OpenxUiCard>
      );
    }

    const handleOnMouseEnter = () => {
      if (isClient && window.innerWidth > 1024) setExpandDetails(true);
    };

    const handleOnMouseLeave = () => {
      if (isClient && window.innerWidth > 1024) setExpandDetails(false);
    };

    return (
      <OpenxUiCard
        href={deepLink}
        className={sumClasses(['openx-ui-card', appending ? 'openx-ui-card-appending' : '', className || ''])}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        onClick={(e) => handleCtaCallBack(e)}
        isMapCard={isMapCard}
      >
        <OpenxUiCardBackground>
          <img src={addWidthToImageUrl()} alt="" loading={isDesktop ? 'eager' : 'lazy'} />
          <OpenxUiCardGradient />
          {isClient && (
            <OpenxUiCardContent className="openx-ui-card-content" isMapCard={isMapCard}>
              <Label {...{ product }} />
              <div className="openx-ui-card-details-mobile">
                <OpenxUiCardDetailsLeft className="openx-ui-card-details-left">
                  <Details
                    {...{ cityName, countryName, expandDetails: false, description, directFlight, isMobile: true }}
                  />
                </OpenxUiCardDetailsLeft>
              </div>
              <div className="openx-ui-card-details-desk">
                <OpenxUiCardDetailsLeft
                  className={sumClasses([
                    'openx-ui-card-details-left',
                    expandDetails ? 'openx-ui-card-details-left-expand' : '',
                  ])}
                >
                  <Details {...{ cityName, countryName, expandDetails, description, directFlight }} />
                  <Rating {...{ accomodationType, stars }} />
                </OpenxUiCardDetailsLeft>
                <div className="openx-ui-card-details-right">{price && <Price {...{ price, nightsText }} />}</div>
              </div>
            </OpenxUiCardContent>
          )}
        </OpenxUiCardBackground>
        <OpenxUiCardDetailsBottomMobile>
          <OpenxUiCardDetailsLeftFlight className="openx-ui-card-details-left-flight">
            {directFlight}
          </OpenxUiCardDetailsLeftFlight>
          {isClient && <Rating {...{ accomodationType, stars }} />}
          {isClient && price && <Price {...{ price, nightsText }} />}
        </OpenxUiCardDetailsBottomMobile>
      </OpenxUiCard>
    );
  },
  (prevProps: OpenXCityCardProps, nextProps: OpenXCityCardProps) => {
    if (prevProps.cityCard.newSearchParams !== nextProps.cityCard.newSearchParams) return false;
    if (prevProps.cityCard.className !== nextProps.cityCard.className) return false;
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    return true;
  }
);

OpenXCityCard.displayName = 'OpenXCityCard';

export default OpenXCityCard;
